import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBRow,
  MDBCol,
  MDBInput,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * BatchFormModal — crear o editar un lote de entradas.
 *
 * Props:
 *  - show: boolean
 *  - batch: objeto TicketBatchDTO (null si es nuevo)
 *  - idRepresentacionFecha: requerido si es creación
 *  - onClose: () => void
 *  - onSaved: (batch) => void
 */
class BatchFormModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState(props.batch);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState(this.initialState(this.props.batch));
    }
  }

  initialState = (batch) => {
    if (batch) {
      return {
        name: batch.name || "",
        numbered: !!batch.numbered,
        numberStart: batch.numberStart != null ? String(batch.numberStart) : "",
        numberEnd: batch.numberEnd != null ? String(batch.numberEnd) : "",
        totalTickets: batch.totalTickets != null ? String(batch.totalTickets) : "",
        price: batch.price != null ? String(batch.price) : "",
        notes: batch.notes || "",
        saving: false,
        isEdit: true,
      };
    }
    return {
      name: "",
      numbered: false,
      numberStart: "",
      numberEnd: "",
      totalTickets: "",
      price: "",
      notes: "",
      saving: false,
      isEdit: false,
    };
  };

  handleClose = () => {
    if (this.state.saving) return;
    if (this.props.onClose) this.props.onClose();
  };

  handleChange = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    this.setState({ [field]: val });
  };

  validate = () => {
    const { isEdit, name, numbered, numberStart, numberEnd, totalTickets } = this.state;
    if (!name.trim()) {
      toast.show({ message: "El nombre del lote es obligatorio", type: "warning" });
      return false;
    }
    if (isEdit) return true;
    if (numbered) {
      const s = parseInt(numberStart, 10);
      const e = parseInt(numberEnd, 10);
      if (!Number.isFinite(s) || !Number.isFinite(e) || s < 1 || e < s) {
        toast.show({
          message: "Número inicial y final no válidos (final ≥ inicial ≥ 1)",
          type: "warning",
        });
        return false;
      }
      if (e - s + 1 > 2000) {
        toast.show({
          message: "El rango supera 2000 entradas. Crea varios lotes más pequeños.",
          type: "warning",
        });
        return false;
      }
    } else {
      const t = parseInt(totalTickets, 10);
      if (!Number.isFinite(t) || t < 1 || t > 2000) {
        toast.show({
          message: "Total de entradas debe estar entre 1 y 2000",
          type: "warning",
        });
        return false;
      }
    }
    return true;
  };

  handleSave = () => {
    if (!this.validate()) return;
    const { isEdit, name, numbered, numberStart, numberEnd, totalTickets, price, notes } = this.state;
    const { batch, idRepresentacionFecha } = this.props;

    const dto = {
      name: name.trim(),
      price: price === "" ? null : Number(price),
      notes: notes.trim() || null,
    };

    if (isEdit) {
      dto.idBatch = batch.idBatch;
    } else {
      dto.idRepresentacionFecha = idRepresentacionFecha;
      dto.numbered = numbered;
      if (numbered) {
        dto.numberStart = parseInt(numberStart, 10);
        dto.numberEnd = parseInt(numberEnd, 10);
        dto.totalTickets = dto.numberEnd - dto.numberStart + 1;
      } else {
        dto.totalTickets = parseInt(totalTickets, 10);
      }
    }

    this.setState({ saving: true });

    const endpoint = isEdit ? "updateTicketBatch" : "createTicketBatch";
    doFetchJson("TEAN", endpoint, {
      method: isEdit ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({
            message: isEdit ? "Lote actualizado" : "Lote creado con éxito",
            type: "success",
          });
          if (this.props.onSaved) this.props.onSaved(json.DATA);
        } else {
          toast.show({
            message: (json && json.MESSAGE) || "No se pudo guardar el lote",
            type: "danger",
          });
        }
      })
      .catch(() => {
        toast.show({ message: "Error de red al guardar el lote", type: "danger" });
      })
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    const { show } = this.props;
    const {
      isEdit,
      name,
      numbered,
      numberStart,
      numberEnd,
      totalTickets,
      price,
      notes,
      saving,
    } = this.state;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog centered size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="ticket-alt" className="me-2" />
                {isEdit ? "Editar lote" : "Nuevo lote de entradas"}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              <MDBRow className="g-3">
                <MDBCol size="12">
                  <MDBInput
                    label="Nombre del lote *"
                    value={name}
                    onChange={this.handleChange("name")}
                  />
                </MDBCol>

                {!isEdit && (
                  <>
                    <MDBCol size="12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="batch-numbered"
                          checked={numbered}
                          onChange={this.handleChange("numbered")}
                        />
                        <label className="form-check-label" htmlFor="batch-numbered">
                          Lote numerado
                        </label>
                      </div>
                    </MDBCol>

                    {numbered ? (
                      <>
                        <MDBCol size="12" md="6">
                          <MDBInput
                            label="Número inicial *"
                            type="number"
                            min="1"
                            value={numberStart}
                            onChange={this.handleChange("numberStart")}
                          />
                        </MDBCol>
                        <MDBCol size="12" md="6">
                          <MDBInput
                            label="Número final *"
                            type="number"
                            min="1"
                            value={numberEnd}
                            onChange={this.handleChange("numberEnd")}
                          />
                        </MDBCol>
                        {numberStart && numberEnd && Number(numberEnd) >= Number(numberStart) && (
                          <MDBCol size="12">
                            <div className="text-muted small">
                              Se generarán {Number(numberEnd) - Number(numberStart) + 1} entradas.
                            </div>
                          </MDBCol>
                        )}
                      </>
                    ) : (
                      <MDBCol size="12" md="6">
                        <MDBInput
                          label="Total de entradas *"
                          type="number"
                          min="1"
                          value={totalTickets}
                          onChange={this.handleChange("totalTickets")}
                        />
                      </MDBCol>
                    )}
                  </>
                )}

                <MDBCol size="12" md="6">
                  <MDBInput
                    label="Precio (€)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={price}
                    onChange={this.handleChange("price")}
                  />
                </MDBCol>

                <MDBCol size="12">
                  <label className="form-label small text-muted">Notas</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={notes}
                    onChange={this.handleChange("notes")}
                  />
                </MDBCol>
              </MDBRow>

              {isEdit && (
                <div className="alert alert-info mt-3 mb-0 small">
                  Al editar un lote sólo pueden cambiarse el nombre, precio y notas.
                  La numeración no se puede modificar una vez generadas las entradas.
                </div>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={this.handleClose} disabled={saving}>
                Cancelar
              </MDBBtn>
              <MDBBtn color="primary" onClick={this.handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <MDBIcon fas icon="save" className="me-1" />
                    {isEdit ? "Guardar cambios" : "Crear lote"}
                  </>
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default BatchFormModal;
